import React, { useRef, useState, useEffect } from 'react';
import shortid from 'shortid';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Flyout, {
  FlyoutPanelContent,
} from 'components/common/omnia/flyout-component';
import ChatBotPanelHeader from 'components/feature/toolkit/chatbot-toolkit-panel/toolkit-chatbot-header-component';
import ChatBotPanelGuidanceMessage from 'components/feature/toolkit/chatbot-toolkit-panel/toolkit-chatbot-guidance-message-component';
import ChatBotPanelFooter from 'components/feature/toolkit/chatbot-toolkit-panel/toolkit-chatbot-panel-footer-component';
import Banner, { BANNER_TYPES } from 'components/common/banner-component';
import { LEFT_PANELS } from 'constants/feature/panel-constants';
import ChatWindow from './toolkit-chatbot-panel-chat-window';
import {
  sendChatBotUserResponse,
  getDocumentIndexingStatus,
  getDocumentIndexingStatusBasedOnProcessType,
  startDocumentIndexing,
  getPromptsLibrary,
} from 'api/chatbot-api';
import {
  createPreviousMessages,
  CHAT_BOT_TYPING_ID,
  chatBotFailApiResponse,
  DOC_INDEXING_STATUS,
  MAX_RETRY_COUNT,
} from './toolkit-chatbot-utils';
import { ChatBotClearSessionAcknowledgeModal } from 'components/common/chat-bot-clear-session-acknowledge-modal';
import { getTranslationByKey } from 'constants/common/feature-common-utils';
import SelectedStatement from 'models/api/selected-statement-model';
import Revision from 'models/api/revision-api-model';
import User from 'models/data/user-model';

export const CHATBOT_PANEL_BLOCK = 'chatbot-panel';
export const CHATBOT_PANEL_BLOCK_ID = 'chatbot-panel-id';
const REQUEST_ID = 'dummy';
const FOOTER_HEIGHT = 72; //px

export const ChatBotPanel = ({
  show,
  statement,
  currentUser,
  geoCode,
  containerCode,
  containerId,
  cbFeedbackEnabled,
  locale,
  languageData,
  selectedStatement = {},
}) => {
  const textAreaRef = useRef(null);
  const bannerRef = useRef(null);
  const footerRef = useRef(null);
  const timerRef = useRef(null);
  const [text, setText] = useState('');
  const [bannerHeight, setBannerHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(FOOTER_HEIGHT);
  const [showSecondaryMessage, setShowSecondaryMessage] = useState(false);
  const [isIndexingCompleted, setIndexingCompleted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [clearSessionModal, setClearSessionModal] = useState(false);
  const [inputFieldDisable, setInputFieldDisable] = useState(false);
  const [documentIndexingData, setDocumentIndexingData] = useState(null);
  const fileKey = `${parseInt(statement.clientId)}_${statement.statementId}_${
    statement.id
  }`;
  const [prompts, setPrompts] = useState([]);

  const getDocumentIndexingData = async () => {
    try {
      const response = await getDocumentIndexingStatusBasedOnProcessType(
        fileKey,
      );
      setDocumentIndexingData(response.result);
      const { status } = response.result;
      if (status === DOC_INDEXING_STATUS.COMPLETED) {
        setIndexingCompleted(true);
      } else {
        setIndexingCompleted(false);
      }
      return response;
    } catch (error) {
      setIndexingCompleted(false);
    }
  };

  const startTimer = () => {
    const checkStatus = async (documentIndexingData) => {
      try {
        if (
          documentIndexingData &&
          documentIndexingData.retryCount >= MAX_RETRY_COUNT
        ) {
          clearInterval(timerRef.current);
          return;
        }
        const response = await getDocumentIndexingData();
        const { status } = response.result;
        if (
          status === DOC_INDEXING_STATUS.COMPLETED ||
          status === DOC_INDEXING_STATUS.FAILED
        ) {
          clearInterval(timerRef.current);
          const promptsParams = {
            file_key: fileKey,
            containerCode,
            geoCode,
          };
          const promptsData = await getPromptsLibrary(promptsParams);
          setPrompts(promptsData || []);
        }
      } catch (error) {
        clearInterval(timerRef.current);
      }
    };

    // Make an initial API call immediately
    checkStatus();

    // Set up the interval to call the API every 10 minutes
    timerRef.current = setInterval(
      () => checkStatus(documentIndexingData),
      600000,
    ); // 600000 ms = 10 minutes
  };

  const handleRetryClick = () => {
    const requestParams = {
      fileKey,
      containerCode,
      geoCode,
      requestId: REQUEST_ID,
      retryCount: documentIndexingData.retryCount + 1,
      containerId: parseInt(containerId.split('-')[0]),
    };
    startDocumentIndexing(requestParams).then(() => {
      startTimer();
    });
  };

  const onPromptSelect = (prompt) => {
    const translatedPrompt = getTranslationByKey({ locale, key: prompt.title });
    setInputFieldDisable(true);
    getDocumentIndexingStatus(fileKey)
      .then((response) => {
        const { status } = response.result;
        if (status === DOC_INDEXING_STATUS.COMPLETED) {
          suggestedPrompts({
            timestamp: Date.now(),
            prompt: prompt.promptId,
            message: translatedPrompt,
            response: '',
            isUser: true,
            id: shortid.generate(),
          });
          setText('');
        } else {
          setIndexingCompleted(false);
          setInputFieldDisable(false);
        }
      })
      .catch((err) => {
        setIndexingCompleted(false);
        setInputFieldDisable(false);
        console.error(err);
      });
  };

  // Function to handle sending chat data based on selection of "prompt" in suggestions in chatbot panel footer
  const suggestedPrompts = (msg) => {
    // if language data is not present and language code is not found, returning default to English
    const statementLanguage = Array.isArray(languageData)
      ? languageData?.find(
          (item) =>
            item.languageCode === selectedStatement?.statement?.languageCode,
        )?.englishName
      : 'English';

    setMessages([...messages, msg]);

    // Add a "Typing..." indicator
    const typingMessage = {
      id: CHAT_BOT_TYPING_ID,
      response: 'Typing...',
      isUser: false,
    };

    const userResponse = {
      userFilterSelected: {
        file_key: fileKey,
      },
      currentMessage: '',
      promptId: msg.prompt,
      language: statementLanguage,
      previousMessages: createPreviousMessages(
        messages.filter((message) => message.id !== CHAT_BOT_TYPING_ID),
      ),
      containerCode,
      geoCode,
      requestId: REQUEST_ID,
    };

    setMessages((prevMessages) => [...prevMessages, typingMessage]);
    // /** payload where user selected a prompt
    //  * {
    //       containerCode: "US",
    //       currentMessage:"",
    //       geoCode:"AME",
    //       promptId:"prompt-1",
    //       language: "English",
    //       previousMessages:[],
    //       requestId:"dummy",
    //       userFilterSelected:{file_key:"8481_22489_25180"},
    //   * }
    // */
    sendChatBotUserResponse({ userResponse })
      .then((data) => {
        setInputFieldDisable(false);
        const { documentSource, response } = data;
        setMessages((prevMessages) => {
          return [
            ...prevMessages.filter(
              (message) => message.id !== CHAT_BOT_TYPING_ID,
            ),
            {
              timestamp: Date.now(),
              response,
              isUser: false,
              documentSource,
              id: shortid.generate(),
              isFeedbackTaken: false,
              isGoodFeedbackTaken: false,
              isBadFeedbackTaken: false,
              human_message: msg.message,
              isValidBotResponse: true,
            },
          ];
        });
      })
      .catch((err) => {
        setInputFieldDisable(false);
        setMessages((prevMessages) => {
          return [
            ...prevMessages.filter(
              (message) => message.id !== CHAT_BOT_TYPING_ID,
            ),
            {
              timestamp: Date.now(),
              response: chatBotFailApiResponse,
              isUser: false,
              id: shortid.generate(),
              isFeedbackTaken: false,
              isGoodFeedbackTaken: false,
              isBadFeedbackTaken: false,
              human_message: msg.message,
              isValidBotResponse: false,
            },
          ];
        });
      });
  };

  useEffect(() => {
    getDocumentIndexingData().then(() => {
      startTimer();
    });

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Handle sending chat data based on click of "Send" button in chatbot panel footer
  // This function will be called when the user clicks the "Send" button
  const handleSendChatData = (msg) => {
    setMessages([...messages, msg]);

    // Add a "Typing..." indicator
    const typingMessage = {
      id: CHAT_BOT_TYPING_ID,
      response: 'Typing...',
      isUser: false,
    };

    const userResponse = {
      userFilterSelected: {
        file_key: fileKey,
      },
      currentMessage: msg.message,
      previousMessages: createPreviousMessages(
        messages.filter((message) => message.id !== CHAT_BOT_TYPING_ID),
      ),
      containerCode,
      geoCode,
      requestId: REQUEST_ID,
    };

    setMessages((prevMessages) => [...prevMessages, typingMessage]);

    sendChatBotUserResponse({ userResponse })
      .then((data) => {
        setInputFieldDisable(false);
        const { documentSource, response } = data;
        setMessages((prevMessages) => {
          return [
            ...prevMessages.filter(
              (message) => message.id !== CHAT_BOT_TYPING_ID,
            ),
            {
              timestamp: Date.now(),
              response,
              isUser: false,
              documentSource,
              id: shortid.generate(),
              isFeedbackTaken: false,
              isGoodFeedbackTaken: false,
              isBadFeedbackTaken: false,
              human_message: msg.message,
              isValidBotResponse: true,
            },
          ];
        });
      })
      .catch((err) => {
        setInputFieldDisable(false);
        setMessages((prevMessages) => {
          return [
            ...prevMessages.filter(
              (message) => message.id !== CHAT_BOT_TYPING_ID,
            ),
            {
              timestamp: Date.now(),
              response: chatBotFailApiResponse,
              isUser: false,
              id: shortid.generate(),
              isFeedbackTaken: false,
              isGoodFeedbackTaken: false,
              isBadFeedbackTaken: false,
              human_message: msg.message,
              isValidBotResponse: false,
            },
          ];
        });
      });
  };

  const onTextAreaChange = (value) => {
    setText(value);
  };

  useEffect(() => {
    if (bannerRef.current) {
      setBannerHeight(bannerRef.current.offsetHeight);
    }
  }, [show]);

  useEffect(() => {
    if (footerRef.current) {
      setFooterHeight(footerRef.current.offsetHeight);
    }
  }, [text.length]);

  // onclick handler for the "Send" button in the footer
  const handleSend = () => {
    if (text.trim()) {
      setInputFieldDisable(true);
      getDocumentIndexingStatus(fileKey)
        .then((response) => {
          const { status } = response.result;
          if (status === DOC_INDEXING_STATUS.COMPLETED) {
            handleSendChatData({
              timestamp: Date.now(),
              message: text,
              response: '',
              isUser: true,
              id: shortid.generate(),
            });
            setText('');
          } else {
            setIndexingCompleted(false);
            setInputFieldDisable(false);
          }
        })
        .catch((err) => {
          setIndexingCompleted(false);
          setInputFieldDisable(false);
          console.log(err);
        });
    }
  };

  const handleClearSession = () => {
    setClearSessionModal(false);
    setShowSecondaryMessage(true);
    setIndexingCompleted(true);
    setMessages([]);
    setText('');
  };

  return (
    <>
      <Flyout className={CHATBOT_PANEL_BLOCK} show={show} position={'left'}>
        <ChatBotPanelHeader
          setClearSessionModal={setClearSessionModal}
          clearSessionModal={clearSessionModal}
          setShowSecondaryMessage={setShowSecondaryMessage}
          setIndexingCompleted={setIndexingCompleted}
          setText={setText}
          messages={messages}
        />
        <FlyoutPanelContent>
          <div className={`${CHATBOT_PANEL_BLOCK}__body`}>
            <div ref={bannerRef}>
              <Banner
                id={`${CHATBOT_PANEL_BLOCK_ID}-disclaimer-banner`}
                className={`${CHATBOT_PANEL_BLOCK}__banner`}
                type={BANNER_TYPES.SUCCESS}
                bannerCopy={'chatbot.disclaimer.banner.message'}
                isInternationalized={true}
              />
            </div>

            <div
              className={`${CHATBOT_PANEL_BLOCK}__chat-section`}
              style={{
                height: `calc(100% - ${bannerHeight + footerHeight}px)`,
              }}
            >
              {/* We will not show the guidance message if message has length */}
              {!text.length && !messages.length && isIndexingCompleted ? (
                <ChatBotPanelGuidanceMessage
                  showSecondaryMessage={showSecondaryMessage}
                />
              ) : (
                <ChatWindow
                  BLOCK={CHATBOT_PANEL_BLOCK}
                  statement={statement}
                  messages={messages}
                  setMessages={setMessages}
                  createPreviousMessages={createPreviousMessages}
                  currentUser={currentUser}
                  containerCode={containerCode}
                  geoCode={geoCode}
                  cbFeedbackEnabled={cbFeedbackEnabled}
                  isIndexingCompleted={isIndexingCompleted}
                  documentIndexingData={documentIndexingData}
                  handleRetryClick={handleRetryClick}
                />
              )}
              {clearSessionModal && (
                <ChatBotClearSessionAcknowledgeModal
                  onClose={() => setClearSessionModal(false)}
                  onSubmit={handleClearSession}
                />
              )}
            </div>
          </div>
          {isIndexingCompleted && (
            <ChatBotPanelFooter
              show={show}
              text={text}
              ref={textAreaRef}
              footerRef={footerRef}
              onTextAreaChange={onTextAreaChange}
              showSecondaryMessage={showSecondaryMessage}
              setShowSecondaryMessage={setShowSecondaryMessage}
              handleSend={handleSend}
              messages={messages}
              inputFieldDisable={inputFieldDisable}
              prompts={prompts}
              onPromptSelect={onPromptSelect}
            />
          )}
        </FlyoutPanelContent>
      </Flyout>
    </>
  );
};

ChatBotPanel.propTypes = {
  /** Indicates if the batch panel should be shown */
  show: PropTypes.bool.isRequired,
  // action method fired to close toolkit-export flyout panel
  /** Language data for the chatbot from statement creation page data model */
  languageData: PropTypes.array,
  /** Selected statement instance */
  selectedStatement: PropTypes.instanceOf(SelectedStatement),
  /** Container ID for the chatbot panel */
  containerId: PropTypes.string,
  /** Indicates if the chatbot feedback feature is enabled */
  cbFeedbackEnabled: PropTypes.bool,
  /** Selected statement's revision instance */
  statement: PropTypes.instanceOf(Revision),
  /** Current user instance */
  currentUser: PropTypes.instanceOf(User),
  /** Container code for the chatbot panel */
  containerCode: PropTypes.string,
  /** Geographical code for the chatbot panel */
  geoCode: PropTypes.string,
};

const mapStateToProps = ({
  data: {
    revision,
    currentUser,
    chatBotFeatureFlags,
    selectedProject: {
      project: { geoCode, containerCode, id: containerId },
    },
    selectedStatement,
  },
  ui: {
    locale,
    statementPage: {
      panels: { left },
      statementCreationPageData: { languageData },
    },
  },
}) => ({
  geoCode,
  containerCode,
  currentUser,
  statement: revision,
  show: left === LEFT_PANELS.CHAT_BOT,
  cbFeedbackEnabled: chatBotFeatureFlags.isChatbotEnabledForProjectFeedBack,
  containerId,
  languageData,
  selectedStatement,
  locale,
});

export default connect(mapStateToProps, null)(ChatBotPanel);
